import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from "@/hooks/errors/use-toast";
import { supabase } from '@/integrations/supabase/client';
import AuthLayout from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [isResending, setIsResending] = useState(false);
  const hasInitializedRef = useRef(false);
  const isInitializingRef = useRef(false);

  useEffect(() => {
    // Show success toast only if we're coming from successful registration
    if (location.state?.showSuccessToast && !hasInitializedRef.current) {
      toast({
        title: "Registration Successful",
        description: "Please check your email to confirm your account.",
        variant: "success",
      });
      
      // 🎯 Set flag IMMEDIATELY to prevent React StrictMode double execution
      hasInitializedRef.current = true;
      
      // Initialize user profile after successful registration
      initializeUserProfile();
    }
  }, [location.state?.showSuccessToast]); // Only depend on specific flag and initialization state

  useEffect(() => {
    // Handle missing email case separately to avoid dependency conflicts
    if (!email) {
      toast({
        title: "Missing Email",
        description: "No email address found. Redirecting to sign up page.",
        variant: "warning",
      });
      
      // Give the user a moment to see the toast before redirecting
      const timer = setTimeout(() => {
        navigate('/signup');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [email, navigate]);

  const initializeUserProfile = async () => {
    try {
      console.log("🔄 Initializing user profile via edge function...");
      
      // Function-level protection: Prevent concurrent executions
      if (isInitializingRef.current) {
        console.log("⚠️ Profile initialization already in progress, skipping...");
        return;
      }
      
      // Set function-level flag
      isInitializingRef.current = true;
      
      // Get user data from location state (passed from signup)
      const userData = location.state?.userData;
      
      if (!userData?.id || !userData?.email) {
        console.error("❌ Missing user data for profile initialization");
        isInitializingRef.current = false; // Reset flag on early exit
        return;
      }
      
      const { data, error } = await supabase.functions.invoke('initialize-user-profile', {
        body: {
          userId: userData.id,
          email: userData.email
        }
      });
      
      if (error) {
        console.error("❌ Profile initialization failed:", error);
        // Don't show error to user - this is background setup
        // They can still use the app, profile will be created on first login
      } else {
        console.log("✅ User profile initialized successfully:", data);
      }
    } catch (error) {
      console.error("💥 Exception during profile initialization:", error);
      // Silent failure - don't disrupt user experience
    } finally {
      // Always reset the function-level flag when done
      isInitializingRef.current = false;
    }
  };

  const handleResendClick = async () => {
    if (!email) {
      toast({
        title: "Error",
        description: "No email address found. Please try signing up again.",
        variant: "error",
      });
      navigate('/signup');
      return;
    }
    
    // Directly resend the email with BotID protection
    await handleResendEmail();
  };

  const handleResendEmail = async () => {
    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {}
      });

      if (error) {
        if (error.message.includes("User already confirmed")) {
          toast({
            title: "Already Verified",
            description: "This email has already been verified. Please sign in.",
            variant: "info",
          });
          navigate('/signin');
          return;
        }
        throw error;
      }

      toast({
        title: "Email Sent",
        description: "A new verification email has been sent to your inbox.",
        variant: "success",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to resend verification email",
        variant: "error",
      });
    } finally {
      setIsResending(false);
    }
  };


  return (
    <AuthLayout
      title="Verify Your Email"
      description={email ? `We've sent a verification email to ${email}. Please check your inbox and click the verification link.` : "Please return to sign up"}
    >
      {email && (
        <div className="space-y-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Didn't receive the email? Check your spam folder or click below to resend.
          </p>

          <Button 
            onClick={handleResendClick}
            disabled={isResending || !email}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-medium py-2.5"
          >
            {isResending ? (
              <>
                <Icon name="Loader2" className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                Resend Verification Email
                <Icon name="RefreshCw" className="ml-2" size={16} />
              </>
            )}
          </Button>

          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            Need to use a different email?{' '}
            <Button 
              variant="link" 
              className="p-0 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300" 
              onClick={() => navigate('/signup')}
            >
              Sign up again
            </Button>
          </p>

        </div>
      )}
    </AuthLayout>
  );
};

export default VerifyEmail; 